import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import i18n from '../../i18n/config';
import { catalogService } from '../../services/catalogService';
import { embedService } from '../../services/embedService';
import { analyticsService } from '../../services/analyticsService';
import { storageService } from '../../services/storageService';
import CatalogMenuDrawer, { hasMappedDescendant } from '../../components/CatalogMenuDrawer';
import CatalogSearchDrawer from '../../components/CatalogSearchDrawer';
import FlipbookViewer from '../catalog-viewer/components/FlipbookViewer';
import ViewerToolbar from '../catalog-viewer/components/ViewerToolbar';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ErrorState from '../../components/ui/ErrorState';

const EmbedViewer = () => {
  const { catalogId } = useParams();
  const [searchParams] = useSearchParams();
  const [catalog, setCatalog] = useState(null);
  const [embedConfig, setEmbedConfig] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const abortControllerRef = useRef(null);
  const historyDebounceRef = useRef(null);

  // Parse URL parameters
  const pageParam = searchParams?.get('page');
  const themeParam = searchParams?.get('theme');
  const navParam = searchParams?.get('nav');
  const shareParam = searchParams?.get('share');
  const zoomParam = searchParams?.get('zoom');
  const fullscreenParam = searchParams?.get('fullscreen');
  const downloadParam = searchParams?.get('download');
  const menuParam = searchParams?.get('menu');
  const searchParam = searchParams?.get('search');
  const langParam = searchParams?.get('lang');
  const modeParam = searchParams?.get('mode');

  useEffect(() => {
    if (pageParam) {
      const pageIndex = parseInt(pageParam) - 1;
      if (!isNaN(pageIndex) && pageIndex >= 0) {
        setCurrentPage(pageIndex);
      }
    }
  }, [pageParam]);

  useEffect(() => {
    // Validate catalogId
    if (!catalogId || catalogId === 'null' || catalogId === 'undefined') {
      setError('No catalog ID provided');
      setLoading(false);
      return;
    }
    
    // Basic UUID format validation (8-4-4-4-12 hex characters)
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex?.test(catalogId)) {
      setError('Invalid catalog ID format');
      setLoading(false);
      return;
    }

    loadCatalogData();

    return () => {
      if (abortControllerRef?.current) {
        abortControllerRef?.current?.abort();
        abortControllerRef.current = null;
      }
      if (historyDebounceRef.current) {
        clearTimeout(historyDebounceRef.current);
        historyDebounceRef.current = null;
      }
    };
  }, [catalogId]);

  const loadCatalogData = async () => {
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoading(true);
      setError(null);

      console.log('[EMBED VIEWER] Loading catalog:', catalogId);

      // BUSINESS RULE: Embed viewer ONLY displays published catalogs
      // Backend enforcement via dedicated service method
      const catalogData = await catalogService?.getCatalogForEmbed(catalogId);

      if (controller?.signal?.aborted) return;

      if (!catalogData) {
        throw new Error('CATALOG_NOT_FOUND');
      }

      console.log('[EMBED VIEWER] Catalog loaded:', {
        id: catalogData?.id,
        title: catalogData?.title,
        status: catalogData?.status,
        totalPages: catalogData?.catalogPages?.length,
        hasPublicImages: catalogData?.catalogPages?.some(p => p?.publicImagePath)
      });

      setCatalog(catalogData);

      // Load embed configuration
      const config = await embedService?.getEmbedConfig(catalogId);
      if (controller?.signal?.aborted) return;
      setEmbedConfig(config);

      console.log('[EMBED VIEWER] Embed config loaded:', config);

      // Track catalog view
      await catalogService?.incrementViews(catalogId);
      
      // Track analytics if enabled
      if (config?.enable_analytics !== false) {
        analyticsService?.trackCatalogView(
          catalogId,
          catalogData?.title,
          catalogData?.companyId
        );
      }

      console.log('[EMBED VIEWER] Catalog ready for display');
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('[EMBED VIEWER] Error loading catalog:', err);
      console.error('[EMBED VIEWER] Error details:', {
        message: err?.message,
        code: err?.code,
        catalogId
      });
      
      // BUSINESS RULE: Provide clear fallback messages for embed context
      let errorMessage = 'Failed to load catalog';
      
      if (err?.message === 'CATALOG_NOT_FOUND') {
        errorMessage = 'This catalog does not exist or has been removed.';
      } else if (err?.message === 'CATALOG_NOT_PUBLISHED') {
        // CRITICAL: Draft catalogs must show clear message in embed
        errorMessage = 'This catalog is not published and cannot be displayed. Please contact the catalog owner.';
      } else if (err?.message === 'CATALOG_IMAGES_UNAVAILABLE') {
        errorMessage = 'This catalog cannot be displayed because the page images are missing. The catalog owner needs to re-upload the PDF.';
      } else {
        errorMessage = err?.message || 'Failed to load catalog. Please try again later.';
      }
      
      setError(errorMessage);
    } finally {
      if (!controller?.signal?.aborted) {
        setLoading(false);
      }
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    const displayPage = newPage === 0 ? 1 : newPage + 1;

    if (historyDebounceRef.current) clearTimeout(historyDebounceRef.current);
    historyDebounceRef.current = setTimeout(() => {
      historyDebounceRef.current = null;
      window.history?.replaceState(null, '', `?page=${displayPage}`);
    }, 300);

    // Track page view if analytics enabled
    if (embedConfig?.enable_analytics !== false && catalog?.companyId) {
      analyticsService?.trackPageView(
        catalogId,
        displayPage,
        catalog?.companyId
      );
    }
  };

  const handlePageJump = (pageIndex) => {
    handlePageChange(pageIndex);
  };

  const handleShare = () => {
    const shareUrl = `${window.location?.origin}/embed/${catalogId}`;

    // Use execCommand fallback — works reliably inside iframes without clipboard-write permission
    try {
      const textarea = document.createElement('textarea');
      textarea.value = shareUrl;
      textarea.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch {
      // Last resort: open the URL in a new tab so the user can copy it manually
      window.open(shareUrl, '_blank');
    }
  };

  const handleDownload = async () => {
    if (!catalog?.publicPdfPath) return;
    try {
      const url = storageService?.getPublicCatalogImageUrl(catalog?.publicPdfPath);
      const response = await fetch(url);
      const blob = await response?.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `${catalog?.title || 'catalogo'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error('[EMBED] Error downloading PDF:', err);
    }
  };

  // Apply theme from URL or config
  const theme = themeParam || embedConfig?.theme || 'light';

  // Resolve viewer mode: query param takes priority over saved config
  const viewerMode = (modeParam === 'flip' || modeParam === 'slider')
    ? modeParam
    : (embedConfig?.viewer_mode === 'slider' ? 'slider' : 'flip');
  const showNav =
    navParam === '1'
      ? true
      : navParam === '0'
      ? false
      : embedConfig?.show_navigation ?? true;
  const showShare =
  shareParam === '1'
    ? true
    : shareParam === '0'
    ? false
    : embedConfig?.show_sharing ?? false;
  const showZoom =
    zoomParam === '1'
      ? true
      : zoomParam === '0'
      ? false
      : embedConfig?.show_zoom ?? true;

  const showFullscreen =
    fullscreenParam === '1'
      ? true
      : fullscreenParam === '0'
      ? false
      : embedConfig?.show_fullscreen ?? true;

  const showDownload =
    downloadParam === '1'
      ? true
      : downloadParam === '0'
      ? false
      : embedConfig?.show_download ?? false;

  const showMenu =
    menuParam === '1'
      ? true
      : menuParam === '0'
      ? false
      : embedConfig?.show_menu ?? false;

  const hasMenuMappings = (catalog?.menuTree || []).some(hasMappedDescendant);

  const showSearch =
    searchParam === '1' ? true :
    searchParam === '0' ? false :
    embedConfig?.show_search ?? false;

  const hasSearchablePages = (catalog?.catalogPages || []).some(p => p?.keywords?.length > 0) || hasMenuMappings;

  useEffect(() => {
    if (langParam && ['es', 'en'].includes(langParam)) {
      i18n.changeLanguage(langParam);
    }
  }, [langParam]);

  useEffect(() => {
    // Apply theme to body
    if (theme === 'dark') {
      document.documentElement?.classList?.add('dark');
    } else {
      document.documentElement?.classList?.remove('dark');
    }
    // Cleanup: remove dark class when embed viewer unmounts
    return () => {
      document.documentElement?.classList?.remove('dark');
    };
  }, [theme]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !catalog) {
    return (
      <div className="flex items-center justify-center h-screen bg-background p-6">
        <ErrorState
          title={error ? 'Failed to Load Catalog' : 'Catalog Not Found'}
          message={error || 'The catalog you are looking for does not exist or is not published.'}
          onRetry={error ? loadCatalogData : null}
          showRetry={!!error}
          retryText="Retry"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Top Toolbar - only show if navigation enabled */}
      {showNav && (
        <ViewerToolbar
          catalog={catalog}
          currentPage={currentPage}
          totalPages={catalog?.catalogPages?.length}
          onPageJump={handlePageJump}
          onShare={showShare ? handleShare : null}
          shareCopied={shareCopied}
          onDownload={showDownload && catalog?.publicPdfPath ? handleDownload : null}
          onSearch={showSearch && hasSearchablePages ? () => setIsSearchOpen(true) : null}
          onMenu={showMenu && hasMenuMappings ? () => setIsMenuOpen(true) : null}
          onEmbed={null}
          embedMode={true}
          companyLogoUrl={catalog?.company?.logoUrl}
        />
      )}

      {/* Main Viewer Area */}
      <div className="flex-1 overflow-hidden">
        <FlipbookViewer
          catalog={catalog}
          pages={catalog?.catalogPages}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          companyId={catalog?.companyId}
          embedMode={true}
          showZoom={showZoom}
          showFullscreen={showFullscreen}
          showNav={showNav}
          viewerMode={viewerMode}
        />
      </div>

      {/* Search Drawer */}
      <CatalogSearchDrawer
        pages={catalog?.catalogPages || []}
        menuTree={catalog?.menuTree || []}
        currentPage={currentPage}
        onNavigate={handlePageChange}
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        embedMode={true}
      />

      {/* Navigation Menu Drawer */}
      <CatalogMenuDrawer
        menuTree={catalog?.menuTree || []}
        currentPage={currentPage}
        onNavigate={handlePageChange}
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        embedMode={true}
      />
    </div>
  );
};

export default EmbedViewer;